// Removed dropdown menu in favor of popover
import LabelManagement, {
  LabelManagementProps,
} from "@/screens/creatives/components/LabelManagement";
import { downloadMedia } from "./DownloadAction";

import {
  RiArrowLeftRightLine,
  RiBrushAiLine,
  RiDownloadLine,
  RiEditBoxLine,
  RiFacebookCircleLine,
  RiInstagramLine,
  RiMore2Line,
  RiStackLine,
  RiTiktokFill,
} from "@remixicon/react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { LuArrowUpFromLine, LuLoaderCircle } from "react-icons/lu";
import { Textarea } from "@/components/ui/textarea";
import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { useGlobalStore } from "@/GlobalStore";
import { generateAd } from "@/screens/creatives/apiService";
import { toast } from "sonner";
import { ClickableToast } from "../../toast/ClickableToast";
import { useNavigate } from "react-router-dom";
import { buildRoute } from "@/router/constants";
import { IconInfoCircle } from "@tabler/icons-react";

interface OptionsActionProps {
  labelManagementProps?: LabelManagementProps | null;
  url?: string | null;
  ad_id: string;
  accountId: string;
  type?: "image" | "video";
  fbPostUrl?: string | null;
  igPostUrl?: string | null;
  tiktokPostUrl?: string | null;
  accountName?: string;
}
const OptionsAction = ({
  labelManagementProps,
  url,
  type,
  ad_id,
  accountId,
  fbPostUrl,
  tiktokPostUrl,
  igPostUrl,
  accountName,
}: OptionsActionProps) => {
  const [createAdPrompt, setCreateAdPrompt] = useState<string>("");
  const [isRecreating, setIsRecreating] = useState(false);
  const [open, setOpen] = useState(false); // create-ad popover
  const [menuOpen, setMenuOpen] = useState(false); // actions popover
  const { selectedAccount } = useGlobalStore();
  const navigate = useNavigate();

  // Ref for finding the Media container ancestor (for glow portal)
  const optionsRef = useRef<HTMLDivElement>(null);
  const [mediaContainer, setMediaContainer] = useState<HTMLElement | null>(null);

  useEffect(() => {
    if (optionsRef.current) {
      const container = optionsRef.current.closest<HTMLElement>(".overflow-hidden");
      if (container) setMediaContainer(container);
    }
  }, []);

  const handleOpenChange = (isOpen: boolean) => {
    setOpen(isOpen);
  };

  const handleMenuOpenChange = (isOpen: boolean) => {
    setMenuOpen(isOpen);
  };

  const handleCreateAd = (adId: string, prompt: string) => {
    if (!accountId) return;
    setIsRecreating(true);
    setMenuOpen(false);

    generateAd(accountId, {
      ad_id: adId,
      prompt: prompt,
    })
      .then(() => {
        toast.custom((t) => (
          <ClickableToast
            icon={IconInfoCircle}
            iconProps={{
              size: 18,
              className: "text-[#2A94F4]",
            }}
            iconBackgroundClassName="bg-[#2A94F414]"
            hasDescription={true}
            messageHighlight="Ad Creation Started"
            descriptionPrefix="Switch to the Creatives tab to view the created ad."
            onClick={() => {
              if (selectedAccount?.id) {
                navigate(`${buildRoute.create(selectedAccount.id)}?type=image`);
                toast.dismiss(t);
              }
            }}
            onDismiss={() => toast.dismiss(t)}
          />
        ));
      })
      .catch((error) => {
        console.error("Failed to generate ad:", error);
      })
      .finally(() => {
        setIsRecreating(false);
      });
  };

  const handleSubmit = () => {
    if (!createAdPrompt.trim()) return;
    const promptToSend = createAdPrompt;
    setCreateAdPrompt("");
    handleOpenChange(false);
    handleCreateAd(ad_id, promptToSend);
  };

  const handleRecreate = () => {
    const defaultPrompt = `Recreate this ad for ${selectedAccount?.name || "our brand"}.`;
    handleCreateAd(ad_id, defaultPrompt);
  };
  const handleDownload = () => {
    if (!url) return;
    void downloadMedia(url, type, undefined, accountName);
    setMenuOpen(false);
  };

  const openLinkInNewTab = (link?: string | null) => {
    if (link && typeof window !== "undefined") {
      window.open(link, "_blank", "noopener,noreferrer");
    }
    setMenuOpen(false);
  };

  const isVideoSequence =
    labelManagementProps?.creativeKind === "video" && !!labelManagementProps?.labelView;
  return (
    <div ref={optionsRef}>
      {/* Clone glow effect – portalled into the Media container */}
      {mediaContainer && createPortal(
        <div className={`ai-glow-wrapper ai-glow--card pointer-events-none absolute inset-0 z-[50] rounded-[inherit] ${isRecreating ? "ai-glow-active" : ""}`}>
          <div className="ai-glow-edge ai-glow-edge-top" />
          <div className="ai-glow-edge ai-glow-edge-right" />
          <div className="ai-glow-edge ai-glow-edge-bottom" />
          <div className="ai-glow-edge ai-glow-edge-left" />
        </div>,
        mediaContainer,
      )}
    <Popover open={menuOpen} onOpenChange={handleMenuOpenChange}>
      <PopoverTrigger>
        <button className="pointer-events-auto bg-[#00000099] backdrop-blur-sm hover:bg-[#000000CC] font-['Helvetica_Neue'] text-[12px] font-normal leading-[16px] text-[#fff] p-[6px] rounded-[8px] transition-opacity cursor-pointer">
          <RiMore2Line size={16} />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-48 p-1">
        {type === "image" && (
          <div className="h-[32px] flex items-center justify-between px-2 py-[6px] hover:bg-hover-gray rounded-sm">
            <button
              type="button"
              onClick={() => handleRecreate()}
              className="flex-1 flex items-center gap-2"
            >
              {isRecreating ? (
                <LuLoaderCircle
                  size={14}
                  className="shrink-0 animate-spin text-brand-primary-400"
                />
              ) : (
                <RiBrushAiLine size={14} className="shrink-0 text-brand-primary-400" />
              )}
              <span className="font-normal not-italic text-[12px] leading-[20px] tracking-[0%] text-[#021859CC]">
                Clone
              </span>
            </button>

            <div className="flex items-center">
              <Popover open={open} onOpenChange={handleOpenChange}>
                <PopoverTrigger>
                  <div className="w-[24px] h-[24px] rounded-[6px] squircle-mask squircle-smooth-[1] squircle-radius-[6px] bg-selected-gray flex items-center justify-center">
                    <RiEditBoxLine size={14} className="text-brand-primary-400" />
                  </div>
                </PopoverTrigger>
                <PopoverContent
                  align="start"
                  side="right"
                  sideOffset={16}
                  className="w-auto p-0 rounded-[12px] border-none overflow-hidden"
                >
                  <div className="inline-flex flex-col gap-[6px] p-[12px] bg-[#18181B]">
                    <div className="flex items-center justify-between">
                      <span className="text-[#FAFAFA] text-[14px] font-medium leading-[20px]">
                        Create Ad
                      </span>
                      <Button
                        onClick={() => handleSubmit()}
                        variant="ghost"
                        className="text-[#A1A1A6] hover:text-white hover:bg-transparent p-0 h-auto flex gap-[6px]"
                      >
                        <LuArrowUpFromLine
                          size={14}
                          className="shrink-0 opacity-80 text-[#FFFFFF]"
                        />
                        <span className="h-[16px] text-[#FAFAFA] hover:text-white hover:bg-transparent p-0 opacity-80 text-[12px] font-normal leading-[20px]">
                          Submit
                        </span>
                      </Button>
                    </div>
                    <Textarea
                      placeholder="Write prompt here..."
                      value={createAdPrompt}
                      onChange={(e) => setCreateAdPrompt(e.target.value)}
                      className="p-0 w-[281px] min-h-[114px] resize-none opacity-80 bg-transparent border-none text-[#FFF] placeholder:text-[#FFF] focus-visible:ring-0 text-[12px] font-normal leading-[20px]"
                    />
                  </div>
                </PopoverContent>
              </Popover>
            </div>
          </div>
        )}
        <button
          onClick={handleDownload}
          className="w-full flex items-center gap-2 px-2 py-[6px] hover:bg-hover-gray rounded-sm"
        >
          <RiDownloadLine size={14} className="shrink-0" color="#9aa3bd" />
          <span className="font-normal not-italic text-[12px] leading-[20px] tracking-[0%] text-[#021859CC]">
            Download
          </span>
        </button>
        {fbPostUrl && (
          <button
            onClick={() => openLinkInNewTab(fbPostUrl)}
            className="w-full flex items-center gap-2 px-2 py-[6px] hover:bg-hover-gray rounded-sm"
          >
            <RiFacebookCircleLine size={14} className="shrink-0" color="#4692F5" />
            <span className="font-normal not-italic text-[12px] leading-[20px] tracking-[0%] text-[#021859CC]">
              Open Facebook
            </span>
          </button>
        )}
        {igPostUrl && (
          <button
            onClick={() => openLinkInNewTab(igPostUrl)}
            className="w-full flex items-center gap-2 px-2 py-[6px] hover:bg-hover-gray rounded-sm"
          >
            <RiInstagramLine size={14} className="shrink-0" color="#F760CA" />
            <span className="font-normal not-italic text-[12px] leading-[20px] tracking-[0%] text-[#021859CC]">
              Open Instagram
            </span>
          </button>
        )}
        {tiktokPostUrl && (
          <button
            onClick={() => openLinkInNewTab(tiktokPostUrl)}
            className="w-full flex items-center gap-2 px-2 py-[6px] hover:bg-hover-gray rounded-sm"
          >
            <RiTiktokFill size={14} className="shrink-0" color="#F760CA" />
            <span className="font-normal not-italic text-[12px] leading-[20px] tracking-[0%] text-[#021859CC]">
              Open Tiktok
            </span>
          </button>
        )}
        {labelManagementProps && (
          <LabelManagement
            {...labelManagementProps}
            handleMenuOpenChange={handleMenuOpenChange}
            customTrigger={
              <div
                className="flex items-center gap-2 px-2 py-[6px] cursor-pointer hover:bg-hover-gray rounded-sm"
                onClick={(e) => {
                  e.stopPropagation();
                }}
              >
                {isVideoSequence ? (
                  <RiStackLine size={14} className="shrink-0" color="#9AA3BD" />
                ) : (
                  <RiArrowLeftRightLine size={14} className="shrink-0" color="#9AA3BD" />
                )}
                <span className="font-normal not-italic text-[12px] leading-[20px] tracking-[0%] text-[#021859CC]">
                  Change {isVideoSequence ? "Sequence" : "Group"}
                </span>
              </div>
            }
          />
        )}
      </PopoverContent>
    </Popover>
    </div>
  );
};

export default OptionsAction;
